// Universal Internal Model (UIM). See ARCHITECTURE.md §6.

import { z } from "zod";

export type EntityId = string;
export type TypeName = string;
export type RefRole = string;

export type DataType =
  | "string" | "number" | "bool" | "datetime" | "duration" | "uri"
  | "geometry" | "geo" | "blob" | "ref" | "enum" | "json";

export type Cardinality = "0..1" | "1" | "0..*" | "1..*";

export interface TypedValue {
  type: DataType;
  value: unknown;
  unit?: string;
  format?: string;
}

export interface PropDef {
  name: string;
  dataType: DataType;
  cardinality: Cardinality;
  optional: boolean;
  enum?: string[];
  range?: [number, number];
  unit?: string;
  description?: string;
}

export interface RefDef {
  name: RefRole;
  target: TypeName | TypeName[];
  cardinality: Cardinality;
  containment: boolean;
  inverse?: RefRole;
  description?: string;
}

export interface TypeDef {
  name: TypeName;
  supertypes: TypeName[];
  abstract?: boolean;
  properties: PropDef[];
  containmentRefs: RefDef[];
  crossRefs: RefDef[];
  annotations: Record<string, unknown>;
}

export interface Ref {
  role: RefRole;
  targetId: EntityId;
  containment: boolean;
}

export interface Entity {
  id: EntityId;
  type: TypeName;
  tags: string[];
  parents: EntityId[];
  props: Record<string, TypedValue>;
  refs: Ref[];
  partial?: boolean;
}

export interface GraphDelta {
  added?: Entity[];
  updated?: Entity[];
  removed?: EntityId[];
  typesAdded?: TypeDef[];
  typesUpdated?: TypeDef[];
}

export interface Query {
  type?: TypeName;
  tag?: string;
  ids?: EntityId[];
  text?: string;
  limit?: number;
  offset?: number;
}

export type Unsub = () => void;

export interface ModelGraph {
  types(): TypeDef[];
  type(name: TypeName): TypeDef | undefined;
  entity(id: EntityId): Entity | undefined;
  rootIds(): EntityId[];
  childrenOf(id: EntityId): Entity[];
  refsOf(id: EntityId): Ref[];
  byType(name: TypeName): Entity[];
  byTag(tag: string): Entity[];
  search(q: string): Entity[];
  observe(cb: (delta: GraphDelta) => void): Unsub;
}

// ---------- Zod runtime validators (validation at adapter→registry boundary) ----------

export const zDataType = z.enum([
  "string","number","bool","datetime","duration","uri",
  "geometry","geo","blob","ref","enum","json",
]);

export const zCardinality = z.enum(["0..1","1","0..*","1..*"]);

// zod treats `z.unknown()` as optional; we need `value` always present in the
// shape, so we keep it `unknown` but assert the wider Zod type.
export const zTypedValue = z.object({
  type: zDataType,
  value: z.unknown(),
  unit: z.string().optional(),
  format: z.string().optional(),
}) as unknown as z.ZodType<TypedValue>;

export const zPropDef: z.ZodType<PropDef> = z.object({
  name: z.string().min(1),
  dataType: zDataType,
  cardinality: zCardinality,
  optional: z.boolean(),
  enum: z.array(z.string()).optional(),
  range: z.tuple([z.number(), z.number()]).optional(),
  unit: z.string().optional(),
  description: z.string().optional(),
});

export const zRefDef: z.ZodType<RefDef> = z.object({
  name: z.string().min(1),
  target: z.union([z.string(), z.array(z.string())]),
  cardinality: zCardinality,
  containment: z.boolean(),
  inverse: z.string().optional(),
  description: z.string().optional(),
});

export const zTypeDef: z.ZodType<TypeDef> = z.object({
  name: z.string().min(1),
  supertypes: z.array(z.string()),
  abstract: z.boolean().optional(),
  properties: z.array(zPropDef),
  containmentRefs: z.array(zRefDef),
  crossRefs: z.array(zRefDef),
  annotations: z.record(z.unknown()),
});

export const zRef: z.ZodType<Ref> = z.object({
  role: z.string().min(1),
  targetId: z.string().min(1),
  containment: z.boolean(),
});

export const zEntity: z.ZodType<Entity> = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  tags: z.array(z.string()),
  parents: z.array(z.string()),
  props: z.record(zTypedValue),
  refs: z.array(zRef),
  partial: z.boolean().optional(),
});
